import { ArrowDownIcon, ArrowUpIcon, EyeIcon, EyeSlashIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import {
  editableOptionFields,
  InputType,
  IVisibleProp,
  loanCustomRangeOptionFields,
  loanCustomSelectOptionFields,
  loanOptionsConstants,
} from 'config'
import { canAddNewField, InputTypeOfLoans, isCustomFieldKey } from 'config/loan.input.fields.constants'
import {
  FieldOrigin,
  ILoanFieldPos,
  isCustomOrigin,
  ISelectRangeOption,
  ISelectValueOption,
} from 'config/loan.input.visibility.type'
import { accountTypes, accountTypeWithBorrower } from 'config/user.type.constants'
import { convertNullToBlank } from 'pages/Resources/GuidelineOverlay/logic'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { loanTypeMap } from 'reducers/loanDetail.reducer'
import { Input2, Modal, Select } from 'stories/components'
import {
  capitalizeFirstLetter,
  getNewSelectionOptions,
  InputConvert,
  InputValidate,
  isEmpty,
  isNull,
  removeComma,
} from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { hasCoborrowerRequiredProps, hasRequiredProps, isInputTypeChangableFieldOrigins } from './inputs'
import { LinkToParty } from './LinkToParty'
import { convertSelectRangeOptionsToMap, generateTitle, generateTitleFromRange } from './utils'

interface IVisiblePropertyModalProps {
  sectionOptions: Record<string, string> | null
  data: IVisibleProp
  field: ILoanFieldPos
  inputProps: InputType
  fieldTitles: Record<string, { title: string; fields: Record<string, string> }>
  fieldInputs: InputTypeOfLoans
  onClose: Function
}

const isVisibleAccountTypesFieldOrigins = (fieldOrigin: FieldOrigin) =>
  [
    FieldOrigin.LoanStructure,
    FieldOrigin.BorrowerInformation,
    FieldOrigin.PropertyInformation,
    FieldOrigin.DeclarationsHMDA,
  ].includes(fieldOrigin) || isCustomOrigin(fieldOrigin)

const defaultInputs = (): Record<string, InputType> => ({
  visibleAccountTypes: {
    inputType: 'multiselect',
    title: 'Visible by Account Type',
    options: {
      self_creator: '[Loan] Creator',
      ...accountTypeWithBorrower(),
    },
    defaultSelected: true,
    value: accountTypes,
    sort: false,
  },
  visibleLoanTypes: {
    inputType: 'multiselect',
    title: 'Visible by Channel',
    options: loanTypeMap,
    defaultSelected: true,
    value: {},
    sort: false,
  },
  section: {
    inputType: 'select',
    title: 'Section',
    required: false,
    hasDefaultOption: true,
    options: {},
  },
  title: {
    inputType: 'text',
    title: 'Title',
    required: false,
    value: true,
  },
  inputType: {
    inputType: 'Select',
    title: 'Input Component Type',
    options: {
      Text: 'Text',
      ToggleButton: 'Toggle Button',
      Select: 'Select',
      TextArea: 'TextArea',
    },
    required: true,
    hasDefaultOption: true,
    visible: false,
  },
  type: {
    inputType: 'select',
    title: 'Input Text Format',
    options: {
      thousandSep: 'Number with Thousand Separator',
      thousandSepNoDecimal: 'Number with Thousand Separator without Decimal',
      date: 'Date',
      time: 'Time',
      email: 'Email',
      phone: 'Phone',
      number: 'Number',
      password: 'Password',
      text: 'Text',
      ssn: 'SSN',
      mers: 'MERS',
      creditScore: 'Credit Score',
      // 'entityTaxID': 'Tax ID', // Default is 'text'
      color: 'Color',
      dollar: 'Dollar',
      percentage: 'Percentage',
    },
    required: true,
    hasDefaultOption: true,
    visible: false,
  },
  customOptions: {
    inputType: 'SelectOptionInput',
    title: 'Select Options',
    value: [],
    required: true,
    visible: false,
  },
  tooltip: {
    inputType: 'textarea',
    title: 'Tooltip',
    rows: 3,
    required: false,
    prefix: '',
    value: '',
  },
  required: {
    inputType: 'ToggleButton',
    title: 'Required',
    required: false,
    value: true,
  },
  coborrowerRequired: {
    inputType: 'ToggleButton',
    title: 'Coborrower Required',
    required: false,
    value: true,
  },
  visibleLogic: {
    inputType: 'visibleLogic',
    title: '',
    required: false,
    value: [],
  },
  linkToParty: {
    inputType: 'Custom',
    title: '',
    required: false,
    value: {},
    visible: false,
  },
})

export const VisiblePropertyModal = ({
  sectionOptions,
  data: _data,
  field,
  inputProps,
  fieldTitles,
  fieldInputs,
  onClose,
}: IVisiblePropertyModalProps) => {
  const [inputs, setInputs] = useState<Record<string, InputType>>(defaultInputs())
  const [data, setData] = useState(_data)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showError, setShowError] = useState(false)
  const { fieldOrigin, fieldKey } = field

  const isEditableOptionField = useMemo(() => editableOptionFields.includes(fieldKey), [fieldKey])
  const showCustomRangeInput = useMemo(() => loanCustomRangeOptionFields.includes(fieldKey), [fieldKey])
  const showCustomSelectInput = useMemo(() => loanCustomSelectOptionFields.includes(fieldKey), [fieldKey])
  const isBankruptcy = useMemo(() => fieldKey === 'bankruptcy', [fieldKey])
  const isMortgageLates = useMemo(() => fieldKey === 'mortgageLates', [fieldKey])
  const isPPP = useMemo(() => fieldKey === 'prepaymentPenalty', [fieldKey])

  const isShowType =
    (isInputTypeChangableFieldOrigins(fieldOrigin) &&
      ['text', 'Text', 'ToggleButton', 'togglebutton', undefined].includes(inputProps.inputType)) ||
    !fieldKey ||
    isCustomFieldKey(fieldKey)

  const valueStep = useMemo(() => {
    switch (fieldKey) {
      case 'loanTerm':
        return 6
      case 'prepaymentPenalty':
        return 12
      default:
        return 1
    }
  }, [fieldKey])

  useEffect(() => {
    if (!data) return

    let newInputs = cloneDeep(inputs)
    Object.keys(newInputs).forEach((key) => {
      newInputs[key].value = (data as any)[key]
    })
    newInputs.required.visible = hasRequiredProps(fieldOrigin)
    newInputs.required.value = data.required === undefined ? inputProps.required : data.required

    newInputs.coborrowerRequired.visible = hasCoborrowerRequiredProps(fieldOrigin)
    newInputs.coborrowerRequired.value =
      data.coborrowerRequired === undefined ? newInputs.required.value : data.coborrowerRequired

    if (!data.title) newInputs.title.value = field.title

    if (canAddNewField(fieldOrigin)) newInputs.title.required = true
    if (isShowType) {
      newInputs.inputType.value = capitalizeFirstLetter(data.inputType || inputProps.inputType)
      newInputs.inputType.visible = true
      newInputs.type.value = data.type || inputProps.type || 'text'
      newInputs.type.visible = true
    } else {
      newInputs.inputType.visible = false
      newInputs.type.visible = false
    }
    newInputs.customOptions.visible = inputProps.inputType == 'Select' && (!fieldKey || isCustomFieldKey(fieldKey))

    newInputs.linkToParty.visible = isCustomOrigin(fieldOrigin)
    newInputs.linkToParty.value = data.linkToParty || {}

    if (data.section && sectionOptions && sectionOptions[data.section] === undefined) {
      const newSectionKey = Object.keys(sectionOptions).find((key) => sectionOptions[key] == `Section ${data.section}`)
      if (newSectionKey) newInputs.section.value = newSectionKey
    }

    ;(newInputs.visibleLogic as any).fieldTitles = fieldTitles
    ;(newInputs.visibleLogic as any).fieldInputs = fieldInputs

    setInputs(newInputs)
  }, [])

  const onChange = (key: string, value: any) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
  }

  const onSubmit = () => {
    let hasError = false
    let newInputs = cloneDeep(inputs)
    let newData: any = cloneDeep(data) || {}
    if (newData?.selectedOptions) newData.selectedOptions = newData.selectedOptions.filter((item: any) => !!item)
    Object.keys(inputs).map((key) => {
      const input = newInputs[key]
      if (input.visible === false) return

      newInputs[key].error = InputValidate({ ...input, value: input.value })
      if (newInputs[key].error?.length) hasError = true
      newData[key] = newInputs[key].value
    })
    setInputs(newInputs)

    const newErrors: Record<string, string> = {}
    if (
      showCustomRangeInput &&
      data?.options &&
      Object.keys(convertSelectRangeOptionsToMap(data.options)).length != data.options.length
    ) {
      newErrors['customRangeSelectOptions'] = 'Duplicated option exists.'
      hasError = true
    }
    if (isPPP && data?.options) {
      data.options.forEach((item: any) => {
        if (isEmpty(item?.value) || isEmpty(item?.subValue)) {
          newErrors['customRangeSelectOptions'] = 'Required field exists.'
          hasError = true
        }

        if (!isEmpty(item?.value) && !isEmpty(item?.subValue)) {
          const raw = item.subValue.replace(/[^0-9]/g, '')
          if (raw.length !== (Math.floor(removeComma(item.value) / 12) || 1)) {
            newErrors['customRangeSelectOptions'] = `${item.value} is not matched to ${item.subValue}`
            hasError = true
          }
        }
      })
    }
    setErrors(newErrors)

    data.options &&
      Array.isArray(data.options) &&
      data.options?.map((item) => {
        if (!item.title) hasError = true
      })
    setShowError(true)

    if (hasError) return

    if (inputs.inputType.value == 'Select' && (!fieldKey || isCustomFieldKey(fieldKey))) {
      newData.options = getNewSelectionOptions([], inputs.customOptions.value)
      newData.hasDefaultOption = true
    }

    if (!canAddNewField(fieldOrigin) && (newData.title == field.title || !newData.title)) delete newData.title
    if (newData.type != data.type) delete newData.defaultValue
    if (newData.type === 'dollar') {
      newData.prefix = '$'
      newData.type = 'thousandSep'
    } else if (newData.type === 'percentage') {
      newData.prefix = '%'
      newData.type = 'number'
    } else delete newData.prefix

    if (newData.inputType == 'ToggleButton' && fieldOrigin == FieldOrigin.DeclarationsHMDA) newData.textLeft = true
    onClose(newData)
  }

  const renderCustomRangeSelectOptions = () => {
    if (!showCustomRangeInput && !showCustomSelectInput) return null

    const onAddOption = () => {
      let newData = cloneDeep(data)
      if (!newData.options) newData.options = []

      if (isMortgageLates) {
        let newFromValue = NaN
        let newToValue = NaN
        let newDays = NaN
        let newMonths = NaN
        if (newData.options.length) {
          const { from, to, days, months } = newData.options[newData.options.length - 1] as ISelectRangeOption
          if (!isNull(from)) newFromValue = Number(from) + 1
          if (!isNull(to)) newToValue = Number(to) + 1
          if (!isNull(days) && days !== undefined) newDays = days
          if (!isNull(months) && months !== undefined) newMonths = months
        }
        ;(newData.options as ISelectRangeOption[]).push({
          title: '',
          days: newDays,
          months: newMonths,
          from: newFromValue,
          to: newToValue,
        } as ISelectRangeOption)
      } else if (isBankruptcy) {
        let newFromValue = NaN
        let newChapter = NaN
        if (newData.options.length) {
          const { to, chapter } = newData.options[newData.options.length - 1] as ISelectRangeOption
          if (!isNull(to)) newFromValue = Number(to) + 1
          if (!isNull(chapter) && chapter !== undefined) newChapter = chapter
        }
        ;(newData.options as ISelectRangeOption[]).push({
          title: '',
          chapter: newChapter,
          from: newFromValue,
          to: NaN,
        } as ISelectRangeOption)
      } else if (showCustomRangeInput) {
        let newFromValue = NaN
        if (newData.options.length) {
          const { to } = newData.options[newData.options.length - 1] as ISelectRangeOption
          if (!isNull(to)) newFromValue = Number(to) + 1
        }
        ;(newData.options as ISelectRangeOption[]).push({
          title: '',
          from: newFromValue,
          to: NaN,
        } as ISelectRangeOption)
      } else
        (newData.options as ISelectValueOption[]).push({
          title: '',
          value: !!newData.options.length
            ? (newData.options[newData.options.length - 1] as ISelectValueOption).value + valueStep
            : 0,
        } as ISelectValueOption)

      const lastIndex = newData.options.length - 1
      newData.options[lastIndex].title = generateTitleFromRange(newData.options[lastIndex], fieldKey)

      setData(newData)
    }

    const onChangeControlOption = (
      idx: number,
      newValue:
        | { [P in keyof ISelectRangeOption]?: NonNullable<ISelectRangeOption[P]> }
        | { [P in keyof ISelectValueOption]?: NonNullable<ISelectValueOption[P]> }
        | null,
    ) => {
      const newData = cloneDeep(data)
      if (!newData.options) return

      if (!newValue) newData.options.splice(idx, 1)
      else
        newData.options[idx] = {
          ...newData.options[idx],
          ...newValue,
        }

      if (newValue?.title === undefined && newValue !== null && ![-1, 1].includes(newValue as any)) {
        newData.options[idx].title = generateTitleFromRange(newData.options[idx], fieldKey)
      }

      setData(newData)
    }

    const onChangeControlOptionOrder = (idx: number, dir: 1 | -1) => {
      const newData = cloneDeep(data)
      if (!newData.options) return

      const newIdx = idx + dir
      if (newIdx < 0 || newIdx >= newData.options.length) return
      else {
        let temp = newData.options[idx]
        newData.options[idx] = newData.options[newIdx]
        newData.options[newIdx] = temp
      }

      setData(newData)
    }

    const { options } = data

    return (
      <div className="grid gap-2">
        <div className="flex justify-between">
          <p className="font-bold text-sm">Custom Range Options</p>
          <span className="btn-icon" onClick={onAddOption}>
            <PlusIcon className="w-4 h-4" />
          </span>
        </div>
        {options &&
          options.map((opt, idx) => {
            const { title, chapter, from, to, days, months, visible, value, subValue } = opt as any
            const isVisible = !!visible || visible === undefined
            const Icon = isVisible ? EyeIcon : EyeSlashIcon
            const titleValue = generateTitle(fieldKey, opt, idx)

            return (
              <div
                className={`grid ${
                  isMortgageLates ? 'grid-cols-6' : isBankruptcy ? 'grid-cols-5' : 'grid-cols-4'
                } gap-2`}
              >
                <div className="col-span-2">
                  <Input2
                    title={titleValue}
                    value={title}
                    key={`Option-${idx}`}
                    placeholder={title}
                    className={isVisible ? 'opacity-100' : 'opacity-30'}
                    onChange={(value: string) => onChangeControlOption(idx, { title: value })}
                    readOnly={isPPP ? false : true}
                    additionalElements={() => (
                      <div className="flex gap-2">
                        <Icon
                          className="w-4 h-4 cursor-pointer"
                          onClick={() => onChangeControlOption(idx, { visible: !isVisible })}
                        />
                        <TrashIcon
                          className="w-4 h-4 cursor-pointer text-red-500"
                          onClick={() => onChangeControlOption(idx, null)}
                        />
                        <ArrowUpIcon
                          className="w-4 h-4 cursor-pointer text-shade-blue"
                          onClick={() => onChangeControlOptionOrder(idx, -1)}
                        />
                        <ArrowDownIcon
                          className="w-4 h-4 cursor-pointer text-shade-blue"
                          onClick={() => onChangeControlOptionOrder(idx, 1)}
                        />
                      </div>
                    )}
                    required
                    error={showError && !title ? 'This field is required.' : ''}
                  />
                </div>
                {showCustomRangeInput ? (
                  <>
                    {isBankruptcy && (
                      <Input2
                        type="text"
                        title={`Chapter`}
                        value={convertNullToBlank(chapter)}
                        key={`Option-${idx}-chapter`}
                        placeholder={'Chapter'}
                        className={isVisible ? 'opacity-100' : 'opacity-30'}
                        onChange={(value: any) => {
                          if (value == '') value = NaN
                          else value = isNaN(Number(value)) ? NaN : Number(value)
                          onChangeControlOption(idx, { chapter: value })
                        }}
                      />
                    )}
                    <Input2
                      type="text"
                      title={`${isMortgageLates ? 'Times ' : ''}From`}
                      value={convertNullToBlank(from)}
                      key={`Option-${idx}-from`}
                      placeholder={'From'}
                      className={isVisible ? 'opacity-100' : 'opacity-30'}
                      onChange={(value: any) => {
                        if (value == '') value = NaN
                        else value = isNaN(Number(value)) ? NaN : Number(value)
                        onChangeControlOption(idx, { from: value })
                      }}
                    />
                    <Input2
                      type="text"
                      title={`${isMortgageLates ? 'Times ' : ''}To`}
                      value={convertNullToBlank(to)}
                      key={`Option-${idx}-to`}
                      placeholder={'To'}
                      className={isVisible ? 'opacity-100' : 'opacity-30'}
                      onChange={(value: any) => {
                        if (value == '') value = NaN
                        else value = isNaN(Number(value)) ? NaN : Number(value)
                        onChangeControlOption(idx, { to: value })
                      }}
                    />

                    {isMortgageLates && (
                      <>
                        <Input2
                          type="text"
                          title={`Late Days`}
                          value={convertNullToBlank(days)}
                          key={`Option-${idx}-days`}
                          placeholder={'Days'}
                          className={isVisible ? 'opacity-100' : 'opacity-30'}
                          onChange={(value: any) => {
                            if (value == '') value = NaN
                            else value = isNaN(Number(value)) ? NaN : Number(value)
                            onChangeControlOption(idx, { days: value })
                          }}
                        />
                        <Input2
                          type="text"
                          title={`Time Period`}
                          value={convertNullToBlank(months)}
                          key={`Option-${idx}-months`}
                          placeholder={'Months'}
                          className={isVisible ? 'opacity-100' : 'opacity-30'}
                          onChange={(value: any) => {
                            if (value == '') value = NaN
                            else value = isNaN(Number(value)) ? NaN : Number(value)
                            onChangeControlOption(idx, { months: value })
                          }}
                        />
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Input2
                      type="text"
                      title={`Value`}
                      value={convertNullToBlank(value)}
                      key={`Option-${idx}-to`}
                      placeholder={'Value'}
                      className={isVisible ? 'opacity-100' : 'opacity-30'}
                      onChange={(value: any) => {
                        if (value == '') value = NaN
                        else value = isNaN(Number(value)) ? NaN : Number(value)
                        onChangeControlOption(idx, { value: value })
                      }}
                    />

                    {isPPP && (
                      <Input2
                        type="text"
                        title={`PPP Structure`}
                        value={subValue}
                        key={`Option-${idx}-subValue`}
                        placeholder={'Value'}
                        className={isVisible ? 'opacity-100' : 'opacity-30'}
                        onChange={(v: any) => {
                          const raw = v.replace(/[^0-9]/g, '')
                          if (raw.length > (Math.floor(removeComma(value) / 12) || 1)) return
                          const formatted = raw.split('').join('/')
                          onChangeControlOption(idx, { subValue: formatted })
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            )
          })}
        {errors['customRangeSelectOptions'] && (
          <p className="text-sm text-rose-700">{errors['customRangeSelectOptions']}</p>
        )}
      </div>
    )
  }

  const renderSelectOptions = () => {
    if (!isEditableOptionField) return <></>
    const { selectedOptions } = data

    const onAdd = () => {
      const newData = cloneDeep(data)
      if (
        !!newData?.selectedOptions &&
        newData.selectedOptions.length == (loanOptionsConstants as any)[fieldKey].length
      )
        return toast(`All options are already included.`, { type: 'error' })
      if (!newData?.selectedOptions) newData.selectedOptions = ['']
      else newData.selectedOptions.push('')
      setData(newData)
    }

    const onChange = (index: number, value: string) => {
      const newData = cloneDeep(data)
      if (!newData?.selectedOptions) return
      if (newData.selectedOptions.some((item, idx) => item == value && idx !== index)) return
      if (newData.selectedOptions[index] == value) return
      newData.selectedOptions[index] = value
      setData(newData)
    }

    const onRemove = (index: number) => {
      const newData = cloneDeep(data)
      if (!newData?.selectedOptions) return
      newData.selectedOptions.splice(index, 1)
      setData(newData)
    }

    const onChangeOrder = (val: string, index: number) => {
      if (!data?.selectedOptions) return
      const nth = Number(val) - 1
      if (nth === index) return
      const newData = cloneDeep(data)
      const removedOne = newData.selectedOptions?.splice(index, 1) || []
      newData.selectedOptions?.splice(nth, 0, removedOne[0])
      setData(newData)
    }

    return (
      <div className="grid gap-2">
        <div className="flex justify-between">
          <p className="font-bold text-sm">Customize Options</p>
          <span className="btn-icon" onClick={onAdd}>
            <PlusIcon className="w-4 h-4" />
          </span>
        </div>

        {!!selectedOptions?.length && (
          <div>
            {selectedOptions.map((item, index) => {
              const orderOptions = selectedOptions.map((_, idx) => (idx + 1).toString())
              return (
                <div key={index} className="flex justify-between items-center my-2">
                  <Select
                    id={`propertyType=${index}`}
                    options={(loanOptionsConstants as any)[fieldKey]}
                    hasDefaultOption={true}
                    value={item}
                    className="w-64 -mb-4"
                    onChange={(value: any) => onChange(index, value)}
                  />
                  <div className="flex gap-2 items-center">
                    <select
                      onChange={(e) => onChangeOrder(e.target.value, index)}
                      value={index + 1}
                      className="block rounded py-1.5 px-2 w-16 text-sm text-gray-900 bg-transparent border border-gray-200 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer"
                    >
                      {orderOptions.map((val) => (
                        <option key={val}>{val}</option>
                      ))}
                    </select>

                    <div
                      className="w-fit p-1 transition-all duration-200 hover:cursor-pointer hover-shadow1 rounded"
                      onClick={() => onRemove(index)}
                    >
                      <TrashIcon className="w-4 h-4 hover:cursor-pointer text-red-700" />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  return (
    <Modal
      isOpen
      title={field.title || 'New Field'}
      titleClass={`${isMortgageLates ? 'w-[600px]' : 'w-144'}`}
      titleOkay="Submit"
      onClose={() => onClose()}
      onOk={onSubmit}
    >
      <div className={`${isMortgageLates ? 'w-[600px]' : 'w-144'}`}>
        {Object.keys(inputs).map((key, index) => {
          if (key == 'type') inputs[key].visible = inputs.inputType.value == 'Text'
          if (key == 'customOptions')
            inputs.customOptions.visible =
              inputs.inputType.value == 'Select' && (!fieldKey || isCustomFieldKey(fieldKey))

          if (inputs[key].visible === false) return null
          if (
            ['visibleAccountTypes', 'visibleLoanTypes'].includes(key) &&
            !isVisibleAccountTypesFieldOrigins(fieldOrigin)
          )
            return null
          if (key == 'section') {
            if (!sectionOptions) return null
            else (inputs[key] as any).options = sectionOptions
          }
          if (key == 'linkToParty')
            (inputs[key] as any).render = (value: any, onChange: Function) => (
              <LinkToParty value={value} onChange={(v: any) => onChange(v)} />
            )

          return (
            <div key={index} className="mb-2">
              <RenderInput input={inputs[key]} Key={key} onChange={onChange} />
            </div>
          )
        })}
        {renderCustomRangeSelectOptions()}
        {renderSelectOptions()}
      </div>
    </Modal>
  )
}
